import { formatISO } from 'date-fns';
import { useMemo } from 'react';
import { gql, useQuery } from 'urql';

import { RangeDate, RgCraAutomationV2HistoryItem } from '@/globalTypes';
import { usePlayer, useQueryParams } from '@/hooks';
import { mapVariables } from '@/utils';
import { makePagination } from '@/utils/pagination';
import { PlayerRgAutomationHistoryQuery } from './__generated__/usePlayerRgAutomationHistory';
import { queryParams } from './queryParams';

export type AutomationHistoryEntry = RgCraAutomationV2HistoryItem;

const QUERY = gql`
  query PlayerRgAutomationHistory(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $playerGlobalId: ID!
    $showSuspended: Boolean
    $processedAt: RangeDate
  ) {
    viewer {
      id
      rgAutomationHistory: rgCraAutomationV2History(
        after: $after
        before: $before
        first: $first
        last: $last
        playerGlobalId: $playerGlobalId
        showSuspended: $showSuspended
        processedAt: $processedAt
      ) {
        edges {
          node {
            eventId
            eventType
            extraInformation
            playerGlobalId
            processedAt
            riskLevelBefore
            riskLevelAfter
            suspendedBy
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          hasPreviousPage
        }
      }
    }
  }
`;

export default function usePlayerRgAutomationHistory() {
  const [query, setQuery] = useQueryParams(queryParams);
  const { playerGlobalId } = usePlayer();

  const processedAt = useMemo((): RangeDate | undefined => {
    if (query.from && query.to) {
      return {
        from: formatISO(query.from),
        to: formatISO(query.to),
      };
    }
    return undefined;
  }, [query.from, query.to]);

  const { from: _from, to: _to, ...queryVariables } = query;

  const [{ data, fetching }, refresh] =
    useQuery<PlayerRgAutomationHistoryQuery>({
      query: QUERY,
      variables: {
        playerGlobalId: playerGlobalId || '',
        ...mapVariables(queryVariables),
        processedAt,
      },
      pause: !playerGlobalId,
    });

  return {
    refresh,
    fetching,
    defaultFilter: query,
    updateFilter: (values: {}) => setQuery(values, 'replaceIn'),
    ...makePagination(
      query,
      setQuery,
      data?.viewer.rgAutomationHistory.pageInfo,
    ),
    history: data?.viewer.rgAutomationHistory?.edges?.map(
      (edge): AutomationHistoryEntry | undefined => edge?.node,
    ),
  };
}
